import { useEffect, useState, useRef, useCallback } from "react";
import { Row, Col, Modal, Button, Card } from "react-bootstrap";
import {
  IoEye,
  IoSyncOutline,
  IoCalendarOutline,
  IoFingerPrintOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import { MdOutlineAccountBalance, MdOutlineNumbers, MdInfo } from "react-icons/md";
import { toast } from "react-toastify";
import { http } from "../auth";

const Bill = ({ data, type, loading, bank, onSelectedIdsChange, list, disabled, show, themeColors, selectedMetodo, selectedBank,
  selectedCurrency, onTotalChange
}) => {
  const [total, setTotal] = useState({});
  const [loadingResume, setLoadingResume] = useState(true);
  const [loadingIcons, setLoadingIcons] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const token = localStorage.getItem("token");
  const [isLoadingFactura, setIsLoadingFactura] = useState(false);

  const mountedRef = useRef(true);
  const consultaTimeoutRef = useRef(null);
  const consultaIdRef = useRef(null);
  const isFirstRender = useRef(true); // Para controlar el primer renderizado

  useEffect(() => {
    return () => { 
      mountedRef.current = false;
      // Limpiar timeout al desmontar
      if (consultaTimeoutRef.current) {
        clearTimeout(consultaTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (onTotalChange && selectedBank && selectedBank !== "") {
      onTotalChange(total?.total_a_pagar || "0");
    }
  }, [total, selectedBank]);

  // Función para formatear números con separador de miles (,) y dos decimales con punto (.)
  const formatNumber = (value) => {
    if (value === undefined || value === null || value === "") return "0.00";

    // Convertir a número y fijar 2 decimales
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00";

    // Formatear con separador de miles (,) y decimales con punto (.)
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleCheckboxChange = (id, idServicio, isChecked) => {
    if (isChecked) {
      setSelectedId(id);
      setSelectedServiceId(idServicio);
    } else {
      setSelectedId(null);
      setSelectedServiceId(null);
    }
    if (onSelectedIdsChange) {
      onSelectedIdsChange(id, idServicio);
    }
  };

  // Función para consultar factura con debounce
  const consultarFacturaConDebounce = useCallback((id_docs) => {
    // Validar que tengamos todos los datos necesarios
    if (!id_docs || !selectedMetodo || selectedMetodo === "") {
      return;
    }

    // Para C2P y P2P, necesitamos el banco seleccionado
    if ((selectedMetodo === "c2p" || selectedMetodo === "p2p") && !selectedBank) {
      return;
    }

    // Limpiar timeout anterior
    if (consultaTimeoutRef.current) {
      clearTimeout(consultaTimeoutRef.current);
    }

    // Verificar si ya se consultó este ID con los mismos parámetros
    const consultaKey = `${id_docs}_${selectedBank}_${selectedCurrency}`;
    if (consultaIdRef.current === consultaKey) {
      return;
    }

    consultaIdRef.current = consultaKey;
    setLoadingResume(false);
    setIsLoadingFactura(true);


    // Agregar un pequeño delay para evitar múltiples llamadas
    consultaTimeoutRef.current = setTimeout(() => {
      http
        .post(
          "/intranet-j/consultar/factura",
          {
            id_docs: id_docs,
            type: type,
            bank: selectedBank,
            currency: selectedCurrency
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        )
        .then((response) => {
          if (mountedRef.current) {
            setTotal(response.data);
            consultaIdRef.current = null;
          }
        })
        .catch((error) => {
          if (!mountedRef.current) return;
          console.error("❌ Error al consultar factura:", error);
          toast.error(error.response?.data?.detail || "Error al consultar la factura");
          setSelectedId(null);
          setSelectedServiceId(null);
          setTotal({});
          consultaIdRef.current = null;
        })
        .finally(() => {
          if (mountedRef.current) {
            setLoadingResume(true);
            setIsLoadingFactura(false);
          }
        });
    }, 300); // Aumentamos el delay a 300ms para dar tiempo a que llegue el banco
  }, [selectedBank, selectedCurrency, type, token, selectedMetodo]);

  // Efecto que maneja la selección inicial
  useEffect(() => {
    // Si hay datos y no hay selección, seleccionar el primero
    if (data?.length > 0 && selectedId === null) {
      const first = data[0];
      setSelectedId(first.id);
      setSelectedServiceId(first.id_servicio?.[0]);
      if (onSelectedIdsChange) {
        onSelectedIdsChange(first.id, first.id_servicio?.[0]);
      }
    }
  }, [data]); // Solo dependemos de data para la selección inicial

  // Efecto separado para la consulta con todas las dependencias necesarias
  useEffect(() => {
    // Verificar que tengamos todo lo necesario para consultar
    const hasRequiredData = 
      selectedId && 
      selectedMetodo && 
      selectedMetodo !== "";

    // Para C2P y P2P, necesitamos el banco
    const needsBank = selectedMetodo === "c2p" || selectedMetodo === "p2p";
    const hasBank = needsBank ? (selectedBank && selectedBank !== "") : true;

    // Para transferencia, necesitamos la moneda
    const needsCurrency = selectedMetodo === "transferencia";
    const hasCurrency = needsCurrency ? (selectedCurrency && selectedCurrency !== "") : true;

    const canConsult = hasRequiredData && hasBank && hasCurrency;

    if (canConsult) {
      consultarFacturaConDebounce(selectedId);
    } else {
    }
  }, [selectedId, selectedMetodo, selectedBank, selectedCurrency, consultarFacturaConDebounce]);

  /*  useEffect(() => {
     setLoading(true);
     http
       .get("/intranet-j/metodos-pago", {
         headers: {
           Authorization: `Bearer ${token}`,
         },
       })
       .then((response) => {
         setLoading(false);
         const facturas = response.data?.facturas || [];
         setData(facturas);
       })
       .catch((error) => {
         console.error("Error fetching data:", error);
         setLoading(false);
       });
   }, []);
  */

  const handleView = async (id) => {
    setLoadingIcons((prev) => ({ ...prev, [id]: true }));

    try {
      const checkResponse = await http.get(`/intranet-j/documento-descargar/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (typeof checkResponse.data === "object" && checkResponse.data.detail) {
        throw new Error(checkResponse.data.detail);
      }

      const pdfResponse = await http.get(`/intranet-j/documento-descargar/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "arraybuffer",
      });

      const blob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, "_blank");

      if (!newWindow) {
        throw new Error("No se pudo abrir una nueva ventana para ver la factura.");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Error al ver la factura";
      toast.error(errorMessage);
    } finally {
      setLoadingIcons((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleCardClick = (facturaId, idServicio) => {
    const isChecked = selectedId === facturaId;
    handleCheckboxChange(facturaId, idServicio, !isChecked);
  };

  const totalFields = [
    { key: "base", label: "Subtotal", showAlways: true },
    { key: "iva", label: "IVA", showCondition: (value) => parseFloat(value || 0) > 0 },
    { key: "igtf", label: "IGTF", showCondition: (value) => parseFloat(value || 0) > 0 },
    { key: "monto_retenido", label: "A retener", showCondition: (value) => parseFloat(value || 0) > 0 },
    { key: "comision", label: "Comisión", showCondition: (value) => parseFloat(value || 0) > 0 },
    {
      key: "monto_abonado",
      label: (
        <div
          className="d-flex align-items-center gap-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowModal(true)}
        >
          <span>Abonado</span>
          <MdInfo className="me-1" size={18} style={{ color: themeColors?.primary }} />
        </div>
      ),
      showCondition: (value) => parseFloat(value || 0) > 0,
    },
  ];

  const getCurrencySymbol = () => {
    if (selectedMetodo === "transferencia" && selectedCurrency === "usd") {
      return "$";
    }
    return "Bs.";
  };

  const getMetodoNombre = (metodo) => {
    const metodosMap = {
      c2p: "Pago móvil (C2P)",
      p2p: "Pago móvil (P2P)",
      transfer: "Transferencia bancaria",
      transferencia: "Transferencia bancaria",
      "pago movil": "Pago móvil",
      "pago móvil": "Pago móvil",
    };

    if (!metodo) return "Método seleccionado";

    if (typeof metodo === "string") {
      return metodosMap[metodo.toLowerCase()] || metodo;
    }

    return metodo?.type || metodo?.tipo || "Método seleccionado";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <div className={show ? "d-none" : ""}>
      {loading ? (
        <div className="placeholder-glow">
          {[1, 2, 3].map((i) => (
            <div key={i} className="formasBorder border-card mt-2 p-3">
              <div className="d-flex align-items-center mb-2">
                <div className="placeholder flex-shrink-0 me-2" style={{ width: 20, height: 20, borderRadius: 4 }}></div>
                <div className="placeholder col-5" style={{ height: 16 }}></div>
              </div>
              <div className="ms-4">
                <div className="placeholder col-4 mb-2" style={{ height: 12 }}></div>
                <div className="placeholder col-8 mb-2" style={{ height: 12 }}></div>
                <div className="placeholder col-6 mb-1" style={{ height: 12 }}></div>
                <div className="placeholder col-7" style={{ height: 12 }}></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Tarjetas de facturas */}
          {data.map((factura, index) => {
            const isLoading = loadingIcons[factura.id] || false;
            const isSelected = selectedId === factura.id;

            return (
              <div
                className={`formasBorder border-card mt-2 
                ${isSelected ? "selected" : ""} 
                ${disabled ? "disabled" : ""}
                `}
                key={factura.id || index}
                onClick={(e) => {
                  if (e.target.type !== "checkbox" && !disabled) {
                    handleCardClick(factura.id, factura.id_servicio[0]);
                  }
                }}
              >
                <Row className="p-2">
                  <Col xs={11} className="col-metodo-2 font-bills-details">
                    <div className="col-metodo">
                      <div className="d-flex align-items-center color-secondary" style={{ marginBottom: "5px" }}>
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selectedId === factura.id}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(factura.id, factura.id_servicio[0], e.target.checked);
                          }}
                          disabled={disabled}
                          style={{
                            cursor: disabled ? "not-allowed" : "pointer",
                            width: "20px",
                            height: "20px",
                            transform: "scale(0.9)"
                          }}
                        />

                        {/* Número de factura */}
                        <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>
                          {factura?.tipo_doc === "FACT" ? "Factura. N°" : "Proforma N°"}{" "}
                          {factura?.tipo_doc === "FACT" ? factura?.nro_fac : factura?.nro_ctl}
                        </span>
                      </div>

                      {/* Segunda fila - Emisión y fecha (debajo del número) */}
                      <div className="emision-row" style={{ marginBottom: "3px", marginLeft: "28px" }}>
                        <span className="emision-bill color-secondary" style={{ fontSize: "0.95em" }}>
                          Emisión {factura?.fecha_emi ? new Date(factura.fecha_emi).toLocaleDateString("es-ES") : "N/A"}
                        </span>
                      </div>

                      {/* Tercera fila - Descripción */}
                      <div
                        className="description-row"
                        style={{
                          marginTop: "3px",
                          marginBottom: "3px",
                          marginLeft: "28px",
                          fontStyle: "italic",
                          color: "#666",
                          fontSize: "0.9em",
                        }}
                      >
                        {factura?.descrip || "Sin descripción"}
                      </div>

                      {/* Cuarta fila - Todos los montos */}
                      <div style={{ marginTop: "5px", marginLeft: "28px" }}>
                        {/* Primera sección: Desglose */}
                        <div>
                          <span className="total-pagar-bill" style={{ fontWeight: "bold" }}>
                            Sub: {formatNumber(factura?.total_bruto)} {factura?.tipo_doc === "FACT" ? "Bs" : "$"}
                          </span>
                          <span className="vertical-divider"></span>
                          <span className="iva-bill">
                            IVA: {formatNumber(factura?.total_iva)} {factura?.tipo_doc === "FACT" ? "Bs" : "$"}
                          </span>
                          <span className="vertical-divider"></span>
                          <div></div>
                          <span className="igtf-bill" >
                            IGTF: {formatNumber(factura?.total_igtf)} {factura?.tipo_doc === "FACT" ? "Bs" : "$"}
                          </span>
                          <span className="vertical-divider"></span>
                        </div>
                        {/* Segunda sección: Comisión, total y tasa */}
                        <div>
                          {factura?.comision && parseFloat(factura.comision) > 0 && (
                            <>
                              <span className="comision-bill">Com: {formatNumber(factura.comision)} Bs</span>
                              <span className="vertical-divider"></span>
                            </>
                          )}
                          <span className="amount-bill">
                            Total: {formatNumber(factura?.total_neto)} {factura?.tipo_doc == "FACT" ? "Bs" : "$"}
                          </span>
                          <span className="vertical-divider"></span>
                          {factura?.tipo_doc === "FACT" && (
                            <span className="tasa-bill">Tasa: {formatNumber(factura?.tasa)} Bs</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xs={1} className="col-view p-0">
                    {isLoading ? (
                      <IoSyncOutline className="loading-icon spin" />
                    ) : (
                      <IoEye
                        className="iconDetails"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(factura?.id);
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </div>
            );
          })}

          {/* Sección de totales - layout estable evita desmontaje abrupto */}
          <div style={{ display: selectedMetodo && selectedId ? "" : "none" }}>
            <h3 className="tituloRealizar text-center-block mt-3 mb-2 color-secondary">
              <strong>A pagar</strong>
            </h3>

            {!loadingResume ? (
              <div className="placeholder-glow box-shadow p-4 mt-3" style={{ borderRadius: "30px", backgroundColor: "var(--fondoBlanco)" }}>
                <div className="d-flex flex-row justify-content-between mb-2">
                  <div className="placeholder col-3" style={{ height: 14 }}></div>
                  <div className="placeholder col-2" style={{ height: 14 }}></div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-2">
                  <div className="placeholder col-2" style={{ height: 14 }}></div>
                  <div className="placeholder col-2" style={{ height: 14 }}></div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-3">
                  <div className="placeholder col-3" style={{ height: 14 }}></div>
                  <div className="placeholder col-2" style={{ height: 14 }}></div>
                </div>
                <hr />
                <div className="d-flex flex-row justify-content-between">
                  <div className="placeholder col-4" style={{ height: 18, borderRadius: 30 }}></div>
                  <div className="placeholder col-2" style={{ height: 18, borderRadius: 30 }}></div>
                </div>
              </div>
            ) : Object.keys(total).length > 0 ? (
              <div
                className="box-shadow summary-pay d-flex flex-column gap-1 p-3 mt-3"
                style={{ borderRadius: "30px", backgroundColor: "#fff" }}
              >
                {totalFields.map(({ key, label, showAlways, showCondition }) => {
                  const value = total?.[key] || "0";
                  const shouldShow = showAlways || (showCondition && showCondition(value));

                  if (!shouldShow) return null;

                  return (
                    <div key={key} className="d-flex flex-row justify-content-between align-items-center py-1">
                      <span>{label}</span>
                      <span>{formatNumber(value)} {getCurrencySymbol()}</span>
                    </div>
                  );
                })}

                <hr className="my-2" />

                <div
                  className="d-flex flex-row justify-content-between pt-1 pb-1 ps-4 pe-4 mt-2 ms-2 me-2"
                  style={{ borderRadius: "30px", backgroundColor: "#6cb42a", color: "#fff" }}
                >
                  <span className="fw-bold">Total a pagar</span>
                  <span>{formatNumber(total?.total_a_pagar)} {getCurrencySymbol()}</span>
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}

      {/* Modal con la información de los abonos */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg" scrollable border="0">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <span>
                Detalles de Abonos
                {selectedMetodo && (
                  <small className="d-block fs-6 fw-light opacity-75">{getMetodoNombre(selectedMetodo)}</small>
                )}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          {total?.abonos && total.abonos.length > 0 ? (
            <div className="d-flex flex-column gap-4">
              {total.abonos.map((abono, index) => (
                <Card key={abono._id || index} className="border-0 shadow-sm overflow-hidden">
                  <Card.Header className="border-0 pt-3 pb-0">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <h5 className="mb-0 fw-bold text-dark">
                          {abono.type} <span className="text-muted fw-light">#{index + 1}</span>
                        </h5>
                      </div>
                      <span className="badge rounded-pill bg-success px-3 py-2">
                        {abono.ammount ? `${formatNumber(abono.ammount)} Bs.` : "0.00 Bs."}
                      </span>
                    </div>
                  </Card.Header>

                  <Card.Body>
                    <Row className="g-3">
                      {/* Banco */}
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <MdOutlineAccountBalance className="text-muted me-2" size={20} />
                          <div>
                            <small className="text-muted d-block">Banco</small>
                            <span className="fw-semibold">{abono.bank || "No especificado"}</span>
                          </div>
                        </div>
                      </Col>

                      {/* Referencia */}
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <MdOutlineNumbers className="text-muted me-2" size={20} />
                          <div>
                            <small className="text-muted d-block">Referencia</small>
                            <span className="fw-semibold text-break">{abono.reference || "N/A"}</span>
                          </div>
                        </div>
                      </Col>

                      {/* Fecha */}
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <IoCalendarOutline className="text-muted me-2" size={20} />
                          <div>
                            <small className="text-muted d-block">Fecha de Operación</small>
                            <span className="fw-semibold">{formatDate(abono.date)}</span>
                          </div>
                        </div>
                      </Col>

                      {/* ID de Registro */}
                      <Col xs={12}>
                        <div className="mt-2 pt-2 border-top d-flex align-items-center">
                          <IoFingerPrintOutline className="text-muted me-2" size={16} />
                          <small className="text-muted font-monospace" style={{ fontSize: "0.75rem" }}>
                            ID Interno: {abono._id || "N/A"}
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <IoInformationCircleOutline size={50} className="text-muted mb-3 opacity-50" />
              <h5 className="text-muted">No hay información de abonos</h5>
              <p className="small text-secondary">Los detalles aparecerán cuando se registre un pago.</p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 bg-light">
          <Button variant="outline-secondary" onClick={handleCloseModal} className="px-4 rounded-pill">
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Bill;